"use client"

import clsx from "clsx"
import React, { ChangeEvent, JSX, useCallback, useId } from "react"
import { useDataAttributes } from "../../hooks"
import { TRadioItem } from "./types"

/**
 * @description 라디오 버튼 그룹. 단일 선택
 * @example
 * <TRadio
 *   items={[{ value: "sm", label: "Small" }, { value: "md", label: "Medium" }]}
 *   value="sm"
 *   onChange={setSize}
 * />
 */
interface TRadioProps<V extends string | number> extends Pick<React.AriaAttributes, "aria-label" | "aria-labelledby"> {
	/** 컨테이너 요소 id */
	id?: string
	/** 추가 CSS 클래스 */
	className?: string
	/** 라디오 항목 배열. { value, label, disabled?, prefix?, suffix? } 형태 */
	items: Array<TRadioItem<V>>
	/** 현재 선택된 값 */
	value?: V
	/**
	 * 라디오 그룹의 name 속성. 그룹 내 모든 input에 공통으로 적용되어 네이티브 라디오 시맨틱
	 * (방향키 이동, 단일 Tab 정지점)이 동작하게 한다. 생략 시 자동 생성된 id를 사용한다.
	 */
	name?: string
	/** 선택 값 변경 콜백. 선택된 값과 해당 아이템 객체 전달 */
	onChange: (value: V, item?: TRadioItem<V>) => void
	/**
	 * "전체" 옵션 표시 여부
	 * @ai-style true=목록 상단에 "전체" 라디오 버튼 추가
	 */
	withAll?: boolean
	/**
	 * "전체" 옵션의 값
	 * @default "all"
	 */
	allValue?: string | number
	/** "전체" 옵션의 data-* 속성 */
	allDataAttributes?: Record<string, string | number | boolean>
	/** 전체 비활성화 */
	disabled?: boolean
	/** 그룹 제목 */
	title?: string | undefined
	/**
	 * 가로 배치 여부
	 * @ai-style true=라디오 버튼을 가로로 나열, false=세로로 나열
	 */
	row?: boolean
}

export const TRadio = React.memo(<V extends string | number>(props: TRadioProps<V>): JSX.Element | null => {
	const {
		id,
		className,
		value,
		items,
		name,
		withAll,
		allValue = "all",
		allDataAttributes,
		disabled,
		title,
		row,
		onChange,
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		...restProps
	} = props

	const generatedId = useId()
	const groupName = name ?? generatedId
	const titleId = `${generatedId}-title`
	const allDataProps = useDataAttributes(allDataAttributes)
	const onItemChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const newValue = event.target.value as V
			const changedItem = items.find((item) => item.value == newValue)
			onChange(newValue, changedItem)
		},
		[onChange, items]
	)

	if (items.length === 0 && !withAll) {
		return null
	}

	return (
		<div id={id} className={clsx("TRadio", className)} {...restProps}>
			{title && (
				<label id={titleId} className="title">
					{title}
				</label>
			)}
			<div className={clsx("group", { row })} role="radiogroup" aria-labelledby={title ? titleId : ariaLabelledBy} aria-label={!title ? ariaLabel : undefined}>
				{withAll && (
					<label data-checked={value === allValue} data-disabled={disabled}>
						<input type="radio" name={groupName} value={allValue} checked={value === allValue} disabled={disabled} {...allDataProps} onChange={onItemChange} />
						<RadioIcon checked={value === allValue} />
						<span className="label">{"전체"}</span>
					</label>
				)}
				{items.map((item) => {
					const isChecked = value === item.value
					const itemDataProps = item.dataAttributes
						? Object.entries(item.dataAttributes).reduce(
								(acc, [key, val]) => {
									const dataKey = key.startsWith("data-") ? key : `data-${key}`
									acc[dataKey] = val
									return acc
								},
								{} as Record<string, string | number | boolean>
							)
						: {}
					const itemDisabled = item.disabled || disabled

					return (
						<label key={String(item.value)} data-checked={isChecked} data-disabled={itemDisabled}>
							<input
								type="radio"
								name={groupName}
								value={String(item.value)}
								checked={isChecked}
								disabled={itemDisabled}
								{...itemDataProps}
								onChange={onItemChange}
							/>
							<RadioIcon checked={isChecked} />
							<span className="label">
								{item.prefix}
								<span>{item.label}</span>
								{item.suffix}
							</span>
						</label>
					)
				})}
			</div>
		</div>
	)
}) as (<V extends string | number>(props: TRadioProps<V>) => JSX.Element | null) & { displayName?: string }

TRadio.displayName = "TRadio"

const RadioIcon = ({ checked }: { checked: boolean }) => {
	return (
		<span className="icon">
			{!checked ? (
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
					<rect x="0.75" y="0.75" width="14.5" height="14.5" rx="7.25" stroke="black" strokeWidth="1.5" />
				</svg>
			) : (
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
					<circle className="line" cx="8" cy="8" r="8" fill="var(--p600)" />
					<circle className="dot" cx="8" cy="8" r="4" fill="var(--white)" />
				</svg>
			)}
		</span>
	)
}
